import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { EnvVars } from '../../../../../../../../../environment';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';
import { Textarea } from '@/new/components/shadcn/textarea';

interface RecordingDetailsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (details: RecordingDetails) => void;
  isLoading?: boolean;
}

export interface RecordingDetails {
  name: string;
  objective: string;
  deponent: string;
  otherSpeakers: string;
  phraseList: string;
}

export function RecordingDetailsDialog({ isOpen, onClose, onConfirm, isLoading }: RecordingDetailsDialogProps) {
  const { t } = useTranslation();

  const isLocal = EnvVars.ENVIRONMENT === 'local';
  const [details, setDetails] = useState<RecordingDetails>(
    isLocal
      ? {
          name: 'Sackler',
          objective:
            'To identify whether Dr Richard Sackler was aware of the addictive potential of OxyContin and to what extent he was involved in the marketing strategies that downplayed these risks.',
          deponent: 'Dr Richard Sackler',
          otherSpeakers: 'Deposing Attorney',
          phraseList: '',
        }
      : {
          name: '',
          objective: '',
          deponent: '',
          otherSpeakers: '',
          phraseList: '',
        },
  );

  const handleConfirm = () => {
    onConfirm(details);
  };

  const handleClose = () => {
    // Reset form on close
    setDetails({
      name: '',
      objective: '',
      deponent: '',
      otherSpeakers: '',
      phraseList: '',
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--circle-fading-plus" />
            Recording Details
          </DialogTitle>
        </DialogHeader>

        <p className="mb-4 text-xs text-gray-500">
          Currently, this feature is recommended for interviews under 60 minutes. For longer interviews, we suggest
          starting a new recording.
        </p>

        <div className="space-y-4">
          <div>
            <Label htmlFor="name">
              Recording Name<span className="text-teal-500">*</span>
            </Label>
            <Input
              id="name"
              type="text"
              placeholder="Enter a name for this recording"
              value={details.name}
              onChange={(e) => setDetails({ ...details, name: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="objective">
              Objective<span className="text-teal-500">*</span>
            </Label>
            <Textarea
              id="objective"
              placeholder="Describe the objective of this deposition, interview, or hearing..."
              value={details.objective}
              onChange={(e) => setDetails({ ...details, objective: e.target.value })}
              className="min-h-[80px]"
            />
          </div>

          <div>
            <Label htmlFor="deponent">
              {capitalizeFirstLetter(t('witness'))}/Interviewee<span className="text-teal-500">*</span>
            </Label>
            <Input
              id="deponent"
              type="text"
              placeholder="The name of the person testifying, or being deposed/interviewed"
              value={details.deponent}
              onChange={(e) => setDetails({ ...details, deponent: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="otherSpeakers">Other Speakers</Label>
            <Input
              id="otherSpeakers"
              type="text"
              placeholder="List other speakers (comma separated)"
              value={details.otherSpeakers}
              onChange={(e) => setDetails({ ...details, otherSpeakers: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="phraseList">
              Key Terms <span className="text-sm font-normal text-gray-500">(for transcription accuracy)</span>
            </Label>
            <Textarea
              id="phraseList"
              placeholder="Enter key places, people, brand names, technical terms, etc."
              value={details.phraseList}
              onChange={(e) => setDetails({ ...details, phraseList: e.target.value })}
              className="min-h-[80px]"
            />
          </div>
        </div>

        <div className="my-4 flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={isLoading || !details.name.trim() || !details.deponent.trim() || !details.objective.trim()}
          >
            {isLoading ? (
              <>
                <Icon className="lucide--loader-circle animate-spin duration-1500" />
                Starting...
              </>
            ) : (
              <>
                <Icon className="lucide--play" />
                Start Recording
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
